(() => {
  class DesktopNotifications {
    constructor(user, { env = (typeof window !== 'undefined' ? window : null), onOpen = () => {} } = {}) {
      this.env = env;
      this.user = user;
      this.onOpen = onOpen;
      this.preferenceKey = 'zirve-desktop-enabled:' + user.id;
      this.receiptKey = 'zirve-desktop-shown:' + user.id + ':' + user.branchId;
      this.enabled = this.read(this.preferenceKey, false) === true;
      this.seen = new Set();
      this.initialized = false;
      this.widgets = [];
      this.open = new Set();
      this.stopped = false;
      this.busy = false;
      this.failure = '';
      this.subscription = Promise.resolve();
      this.streamOpened = () => this.sync(true);
      this.changed = () => {
        this.enabled = this.read(this.preferenceKey, false) === true;
        this.render();
        this.sync();
      };
      this.accountChanged = (event) => {
        if (
          event.detail.id !== user.id ||
          event.detail.branchId !== user.branchId
        )
          this.stop();
      };
      env.document.addEventListener('desktop-stream-opened', this.streamOpened);
      env.document.addEventListener('desktop-notifications-stop', () =>
        this.stop(),
      );
      env.document.addEventListener('account-changed', this.accountChanged);
      env.document.addEventListener('language-changed', () => this.render());
      env.addEventListener('storage', (event) => {
        if (event.key === this.preferenceKey) this.changed();
      });
      env.addEventListener('focus', this.changed);
      env.addEventListener('pagehide', () => this.stop());
      env.navigator.permissions
        ?.query?.({ name: 'notifications' })
        ?.then((permission) => {
          if (this.stopped) return;
          this.permission = permission;
          permission.addEventListener('change', this.changed);
        })
        .catch(() => {});
      this.sync();
    }
    read(key, fallback) {
      try {
        return JSON.parse(this.env.localStorage.getItem(key)) ?? fallback;
      } catch {
        return fallback;
      }
    }
    write(key, value) {
      try {
        this.env.localStorage.setItem(key, JSON.stringify(value));
      } catch {}
    }
    supported() {
      return (
        typeof this.env.Notification === 'function' &&
        this.env.isSecureContext &&
        !this.env.Capacitor?.isNativePlatform?.()
      );
    }
    active() {
      return (
        this.supported() &&
        this.enabled &&
        this.env.Notification.permission === 'granted' &&
        !this.stopped
      );
    }
    text(value) {
      return this.env.I18n?.t(value) || value;
    }
    mount(host) {
      if (!host) return;
      const widget = this.env.document.createElement('div');
      widget.className = 'desktop-notification-controls';
      widget.innerHTML =
        '<p class="desktop-notification-status" role="status" aria-live="polite"></p><div class="desktop-notification-actions"><button type="button" class="desktop-notification-toggle" data-icon="doorbell"></button><button type="button" class="desktop-notification-test" data-icon="comments">Deneme bildirimi gönder</button></div>';
      host.append(widget);
      this.widgets.push(widget);
      widget
        .querySelector('.desktop-notification-toggle')
        .addEventListener('click', () => this.toggle());
      widget
        .querySelector('.desktop-notification-test')
        .addEventListener('click', () => this.test());
      this.render();
    }
    render() {
      let status = this.active()
        ? 'Masaüstü bildirimleri açık. Site açıkken yeni bildirimler Windows’ta da gösterilir.'
        : 'Masaüstü bildirimleri kapalı. Açmak için tarayıcının bildirim iznini verin.';
      if (!this.supported())
        status = this.env.Capacitor?.isNativePlatform?.()
          ? 'Telefon bildirimleri uygulamanın bildirim izniyle yönetilir.'
          : !this.env.isSecureContext
            ? 'Masaüstü bildirimleri için bu bilgisayarda localhost / 127.0.0.1 adresini veya HTTPS kullanın.'
            : 'Bu tarayıcı masaüstü bildirimlerini desteklemiyor.';
      else if (this.env.Notification.permission === 'denied')
        status =
          'Tarayıcı bildirimleri engellenmiş. Adres çubuğundaki site izinlerinden bildirimlere izin verin.';
      for (const widget of this.widgets) {
        widget.querySelector('.desktop-notification-status').textContent =
          this.failure || status;
        const toggle = widget.querySelector('.desktop-notification-toggle');
        toggle.textContent = this.active()
          ? 'Masaüstü bildirimlerini kapat'
          : 'Masaüstü bildirimlerini aç';
        toggle.disabled =
          this.busy ||
          !this.supported() ||
          this.stopped ||
          this.env.Notification.permission === 'denied';
        widget.querySelector('.desktop-notification-test').disabled =
          this.busy || !this.active();
      }
    }
    sync(force = false) {
      this.subscription = this.subscription
        .catch(() => {})
        .then(async () => {
          const enabled = this.active();
          if (!force && this.lastSubscription === enabled) return;
          try {
            const result = await this.env.App.request(
              '/api/notifications/desktop',
              {
                method: 'POST',
                body: JSON.stringify({
                  enabled,
                  clientId: this.env.App.desktopClientId,
                }),
              },
            );
            this.lastSubscription = result.connected ? enabled : undefined;
          } catch {
            this.lastSubscription = undefined;
          }
        });
      return this.subscription;
    }
    async toggle() {
      if (this.busy || this.stopped || !this.supported()) return;
      this.busy = true;
      this.failure = '';
      this.render();
      try {
        if (this.active()) this.enabled = false;
        else {
          let permission = this.env.Notification.permission;
          if (permission === 'default')
            permission = await this.env.Notification.requestPermission();
          this.enabled = permission === 'granted';
        }
        this.write(this.preferenceKey, this.enabled);
        if (!this.enabled) this.closeAll();
        await this.sync();
      } catch {
        this.failure =
          'Bildirim izni alınamadı. Tarayıcı izinlerini kontrol edin.';
      } finally {
        this.busy = false;
        this.render();
      }
    }
    make(title, options) {
      const notice = new this.env.Notification(title, {
        icon: '/assets/logo.png',
        lang: this.env.I18n?.current || 'tr',
        dir: this.env.I18n?.current === 'ar' ? 'rtl' : 'ltr',
        ...options,
      });
      this.open.add(notice);
      notice.onclose = () => this.open.delete(notice);
      return notice;
    }
    test() {
      if (!this.active()) return;
      try {
        this.make('Zirve Döküm', {
          body: this.text(
            'Masaüstü bildirimleri hazır. Yeni bildirimler burada görünecek.',
          ),
          tag: 'zirve-desktop-test',
        });
      } catch {
        this.failure =
          'Bildirim gösterilemedi. Tarayıcı ve Windows bildirim ayarlarını kontrol edin.';
        this.render();
      }
    }
    async consume(items) {
      if (this.stopped) return;
      if (!this.initialized) {
        items.forEach((item) => this.seen.add(item.id));
        this.initialized = true;
        return;
      }
      const fresh = items
        .filter((item) => !this.seen.has(item.id) && !item.readAt)
        .reverse();
      items.forEach((item) => this.seen.add(item.id));
      if (!this.active()) return;
      for (const item of fresh) {
        const show = async () => {
          if (
            !this.active() ||
            this.env.App.user?.id !== this.user.id ||
            this.env.App.user?.branchId !== this.user.branchId
          )
            return;
          const receipts = this.read(this.receiptKey, []);
          if (receipts.includes(item.id)) return;
          try {
            const title =
              (item.customerName ? item.customerName + ' · ' : '') +
              this.text(item.title);
            const notice = this.make(title, {
              body: this.text(item.body),
              tag:
                'zirve-' +
                this.user.id +
                '-' +
                this.user.branchId +
                '-' +
                item.id,
            });
            this.write(this.receiptKey, [...receipts, item.id].slice(-500));
            notice.onclick = async () => {
              notice.close();
              if (this.stopped) return;
              try {
                const current = await this.env.App.request('/api/me');
                if (
                  current.user.id !== this.user.id ||
                  current.user.branchId !== this.user.branchId
                )
                  return;
                this.env.focus();
                await this.onOpen(item.id);
              } catch {}
            };
          } catch {
            this.failure =
              'Bildirim gösterilemedi. Tarayıcı ve Windows bildirim ayarlarını kontrol edin.';
            this.render();
          }
        };
        // All tabs share a receipt under an exclusive lock; one notification per account.
        if (this.env.navigator.locks)
          await this.env.navigator.locks.request(this.receiptKey, show);
        else await show();
      }
    }
    closeAll() {
      for (const notice of this.open) notice.close();
      this.open.clear();
    }
    stop() {
      this.stopped = true;
      this.closeAll();
      this.render();
      this.sync();
    }
    dispose() {
      this.stop();
      this.permission?.removeEventListener('change', this.changed);
      this.widgets = [];
    }
  }
  if (typeof module !== 'undefined' && module.exports)
    module.exports = { DesktopNotifications };
  if (typeof window !== 'undefined') window.DesktopNotifications = DesktopNotifications;
})();

const DesktopNotificationsClass = typeof window !== 'undefined' ? window.DesktopNotifications : null;
export default { DesktopNotifications: DesktopNotificationsClass };
export { DesktopNotificationsClass as DesktopNotifications };
